import { DATE_CONDITIONS, NUMERIC_CONDITIONS } from "./filterTypes";
import type { Dayjs } from "dayjs";
import type {
  CategoricalExclusionCondition,
  CategoricalFilterMode,
  CategoricalFilterValue,
  DateCondition,
  DateFilterCondition,
  DateFilterMode,
  DateInitialValues,
  FilterConditionValue,
  FilterType,
  FilterValue,
  NumericCondition,
  NumericFilterCondition,
  RelativeDatePreset,
  TextCondition,
  TextFilterCondition,
} from "./filterTypes";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { formatGeoFilterDisplayText, isGeoFilter } from "./geoFilterUtil";

dayjs.extend(quarterOfYear);

export const formatFilterLabel = (value: unknown): string => {
  if (value == null) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

export const createNumericFilter = (
  condition: NumericCondition,
  value?: number,
  min?: number,
  max?: number,
): NumericFilterCondition => ({
  condition,
  ...(value !== undefined && { value }),
  ...(min !== undefined && { min }),
  ...(max !== undefined && { max }),
});

export const createDateFilter = (
  condition: DateCondition,
  value?: Date | string | Dayjs,
  startDate?: Date | string | Dayjs,
  endDate?: Date | string | Dayjs,
  options?: {
    mode?: DateFilterMode;
    relativePreset?: RelativeDatePreset;
  },
): DateFilterCondition => ({
  condition,
  ...(value !== undefined && { value }),
  ...(startDate !== undefined && { startDate }),
  ...(endDate !== undefined && { endDate }),
  ...(options?.mode !== undefined && { mode: options.mode }),
  ...(options?.relativePreset !== undefined && {
    relativePreset: options.relativePreset,
  }),
});

export const buildRelativeDateFilterOptions = (
  mode: DateFilterMode,
  relativePreset: RelativeDatePreset | null,
): { mode: "relative"; relativePreset?: RelativeDatePreset } | undefined => {
  if (mode !== "relative") return undefined;

  return relativePreset
    ? { mode: "relative", relativePreset }
    : { mode: "relative" };
};

export const createTextFilter = (
  condition: TextCondition,
  value: string,
): TextFilterCondition => ({
  condition,
  value,
});

export const RELATIVE_DATE_PRESET_LABELS: Record<RelativeDatePreset, string> = {
  today: "Today",
  this_week: "This week",
  last_week: "Last week",
  next_30_days: "Next 30 Days",
  last_30_days: "Last 30 Days",
  next_90_days: "Next 90 Days",
  last_90_days: "Last 90 Days",
  current_quarter: "Current Quarter",
  ytd: "YTD",
};

export const RELATIVE_DATE_PRESETS: {
  value: RelativeDatePreset;
  label: string;
}[] = Object.entries(RELATIVE_DATE_PRESET_LABELS).map(([value, label]) => ({
  value: value as RelativeDatePreset,
  label,
}));

export const getRelativeDatePresetRange = (
  preset: RelativeDatePreset,
  referenceDate = dayjs(),
): [Dayjs, Dayjs] => {
  const base = referenceDate;

  switch (preset) {
    case "today":
      return [base.startOf("day"), base.endOf("day")];
    case "this_week":
      return [base.startOf("week"), base.endOf("week")];
    case "last_week": {
      const lastWeek = base.subtract(1, "week");
      return [lastWeek.startOf("week"), lastWeek.endOf("week")];
    }
    case "next_30_days":
      return [base.startOf("day"), base.add(29, "day").endOf("day")]; // 30 days inclusive of today
    case "last_30_days":
      return [base.subtract(29, "day").startOf("day"), base.endOf("day")]; // 30 days inclusive of today
    case "next_90_days":
      return [base.startOf("day"), base.add(89, "day").endOf("day")]; // 90 days inclusive of today
    case "last_90_days":
      return [base.subtract(89, "day").startOf("day"), base.endOf("day")]; // 90 days inclusive of today
    case "current_quarter":
      return [base.startOf("quarter"), base.endOf("quarter")];
    case "ytd":
      return [base.startOf("year"), base.endOf("day")]; // Jan 1 through today
    default:
      return [base.startOf("day"), base.endOf("day")];
  }
};

export const getRelativeDatePresetFromRange = (
  range: [Dayjs, Dayjs],
  referenceDate = dayjs(),
): RelativeDatePreset | null => {
  const targetStart = range[0]?.startOf("day");
  const targetEnd = range[1]?.startOf("day");

  const matchedPreset = RELATIVE_DATE_PRESETS.find(({ value }) => {
    const [presetStart, presetEnd] = getRelativeDatePresetRange(
      value,
      referenceDate,
    );
    return (
      presetStart.isSame(targetStart, "day") &&
      presetEnd.isSame(targetEnd, "day")
    );
  });

  return matchedPreset?.value ?? null;
};

const NUMERIC_CONDITIONS_REQUIRING_VALUE = new Set<NumericCondition>([
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
  "greater_than_or_equal",
  "less_than_or_equal",
]);

export const isCategoricalFilter = (
  filterValue: FilterConditionValue,
): filterValue is CategoricalFilterValue => {
  return (
    Array.isArray(filterValue) &&
    (filterValue.length === 0 ||
      filterValue[0] === null || // allow null
      typeof filterValue[0] !== "object" ||
      !("condition" in (filterValue[0] as any)))
  );
};

export const isCategoricalExclusionFilter = (
  filterValue: FilterConditionValue,
): filterValue is CategoricalExclusionCondition[] => {
  return (
    Array.isArray(filterValue) &&
    filterValue.length > 0 &&
    typeof (filterValue as any)[0] === "object" &&
    (filterValue as any)[0] !== null &&
    (filterValue as any)[0].condition === "not_in" &&
    Array.isArray((filterValue as any)[0].values)
  );
};

export const getCategoricalFilterValues = (
  filterValue?: FilterConditionValue,
): FilterValue[] => {
  if (!filterValue) return [];
  if (isCategoricalExclusionFilter(filterValue)) return filterValue[0].values;
  if (isCategoricalFilter(filterValue)) return filterValue;
  return [];
};

export const getCategoricalFilterMode = (
  filterValue?: FilterConditionValue,
): CategoricalFilterMode => {
  return filterValue && isCategoricalExclusionFilter(filterValue)
    ? "exclude"
    : "include";
};

export const createCategoricalFilterValue = (
  values: FilterValue[],
  mode: CategoricalFilterMode,
): FilterConditionValue => {
  return mode === "exclude" ? [{ condition: "not_in", values }] : values;
};

export const isNumericFilter = (
  filterValue: FilterConditionValue,
): filterValue is NumericFilterCondition[] => {
  return (
    Array.isArray(filterValue) &&
    filterValue.length > 0 &&
    typeof (filterValue as any)[0] === "object" &&
    (filterValue as any)[0] !== null &&
    "condition" in (filterValue as any)[0] &&
    NUMERIC_CONDITIONS.includes((filterValue as any)[0].condition)
  );
};

export const isDateFilter = (
  filterValue: FilterConditionValue,
): filterValue is DateFilterCondition[] => {
  return (
    Array.isArray(filterValue) &&
    filterValue.length > 0 &&
    typeof (filterValue as any)[0] === "object" &&
    (filterValue as any)[0] !== null &&
    "condition" in (filterValue as any)[0] &&
    DATE_CONDITIONS.includes((filterValue as any)[0].condition)
  );
};

export const getFilterDisplayText = (
  filterValue: FilterConditionValue,
  dataType: FilterType,
): string => {
  if (isGeoFilter(filterValue)) {
    return formatGeoFilterDisplayText(filterValue[0]);
  }

  if (isCategoricalExclusionFilter(filterValue)) {
    const excludedValues = filterValue[0].values;
    return excludedValues.length === 1
      ? `excludes: ${formatFilterLabel(excludedValues[0])}`
      : `excludes: ${excludedValues.length}`;
  }

  if (isCategoricalFilter(filterValue)) {
    return filterValue.length === 1
      ? formatFilterLabel(filterValue[0])
      : `${filterValue.length}`;
  }

  if (isNumericFilter(filterValue) && dataType === "numeric") {
    const condition = filterValue[0];
    switch (condition.condition) {
      case "equals":
        return `= ${condition.value}`;
      case "not_equals":
        return `≠ ${condition.value}`;
      case "greater_than":
        return `> ${condition.value}`;
      case "less_than":
        return `< ${condition.value}`;
      case "greater_than_or_equal":
        return `≥ ${condition.value}`;
      case "less_than_or_equal":
        return `≤ ${condition.value}`;
      case "has_data":
        return "Has data";
      case "no_data":
        return "No data";
      case "between":
        if (condition.min !== undefined && condition.max !== undefined) {
          return `${condition.min} - ${condition.max}`;
        }
        return "";
      default:
        return "1";
    }
  }

  if (isDateFilter(filterValue) && dataType === "datetime") {
    const condition = filterValue[0];
    const isRelative =
      condition.mode === "relative" || condition.relativePreset != null;

    // Helper function to format date
    const formatDate = (date: any, isRange = false): string => {
      if (!date) return "";

      const format = isRange ? "MM/DD/YY" : "MM/DD/YYYY";

      // Handle Dayjs objects
      if (
        date &&
        typeof date === "object" &&
        typeof date.format === "function"
      ) {
        return date.format(format);
      }

      // Handle Date objects
      if (date instanceof Date) {
        if (isNaN(date.getTime())) return "";
        return isRange
          ? date.toLocaleDateString("en-US", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            })
          : date.toLocaleDateString();
      }

      // Handle string dates
      if (typeof date === "string") {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return "";
        return isRange
          ? dateObj.toLocaleDateString("en-US", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            })
          : dateObj.toLocaleDateString();
      }

      return "";
    };

    switch (condition.condition) {
      case "no_data":
        return "Null";
      case "equals":
        return condition.value ? formatDate(condition.value) : "On";
      case "between":
        if (isRelative && condition.relativePreset) {
          return `Relative to ${RELATIVE_DATE_PRESET_LABELS[condition.relativePreset]}`;
        }
        if (condition.startDate && condition.endDate) {
          return `${formatDate(condition.startDate, true)} - ${formatDate(
            condition.endDate,
            true,
          )}`;
        }
        return "";
      case "before":
        return condition.value ? `< ${formatDate(condition.value)}` : "Before";
      case "after":
        return condition.value ? `> ${formatDate(condition.value)}` : "After";
      default:
        return "1";
    }
  }

  return "1";
};

export const formatPlotFilterValue = (
  filterValue: FilterConditionValue,
  dataType: FilterType,
): string => {
  if (isCategoricalExclusionFilter(filterValue)) {
    const excludedValues = filterValue[0].values;
    if (excludedValues.length > 1) {
      return `excludes: ${excludedValues.map(formatFilterLabel).join(", ")}`;
    }
  }

  if (isCategoricalFilter(filterValue) && filterValue.length > 1) {
    return filterValue.map(formatFilterLabel).join(", ");
  }

  return getFilterDisplayText(filterValue, dataType);
};

export const formatFilterKey = (
  key: string,
  columnsTitleCreator?: (column: string) => string,
): string => {
  return columnsTitleCreator ? columnsTitleCreator(key) : key;
};

export const hasFilterValue = (filterValue?: FilterConditionValue): boolean => {
  if (!filterValue) return false;
  if (isCategoricalExclusionFilter(filterValue)) {
    return filterValue[0].values.length > 0;
  }
  if (isCategoricalFilter(filterValue)) return filterValue.length > 0;
  return Array.isArray(filterValue) && filterValue.length > 0;
};

export const canApplyNumericFilter = (
  condition: NumericCondition | null,
  value: number | { min: number; max: number } | null | undefined,
): boolean => {
  if (!condition) return false;

  if (condition === "between") {
    if (!value || typeof value !== "object") {
      return false;
    }
    const { min, max } = value as { min: number; max: number };
    const minIsValid = typeof min === "number" && !Number.isNaN(min);
    const maxIsValid = typeof max === "number" && !Number.isNaN(max);
    return minIsValid && maxIsValid;
  }

  const conditionRequiresValue =
    NUMERIC_CONDITIONS_REQUIRING_VALUE.has(condition);

  if (!conditionRequiresValue) {
    return true;
  }

  return typeof value === "number" && !Number.isNaN(value);
};

export const getInitialNumericValues = (filterValue?: FilterConditionValue) => {
  if (filterValue && isNumericFilter(filterValue)) {
    const condition = filterValue[0];
    if (condition.condition === "between") {
      return {
        condition: condition.condition,
        min: condition.min ?? null,
        max: condition.max ?? null,
        value: null,
      };
    }
    return {
      condition: condition.condition,
      value: condition.value ?? null,
      min: null,
      max: null,
    };
  }
  return { condition: null, value: null, min: null, max: null };
};

export const getInitialDateValues = (
  filterValue?: FilterConditionValue,
): DateInitialValues => {
  if (filterValue && isDateFilter(filterValue)) {
    const condition = filterValue[0];

    const isNullCondition = condition.condition === "no_data";

    if (isNullCondition) {
      return {
        condition: "no_data",
        mode: "absolute",
        relativePreset: null,
        date: null,
        range: null,
      };
    }

    const isRelative =
      condition.mode === "relative" || condition.relativePreset != null;
    const relativePreset = condition.relativePreset ?? null;

    const convertToDatejs = (dateValue: Date | string | Dayjs | undefined) => {
      if (!dateValue) return null;
      return dayjs(dateValue);
    };

    const relativeRange =
      isRelative && relativePreset
        ? getRelativeDatePresetRange(relativePreset)
        : null;

    return {
      condition: condition.condition,
      mode: isRelative ? "relative" : "absolute",
      relativePreset,
      date:
        relativeRange && relativeRange[0] && relativeRange[1]
          ? null
          : convertToDatejs(condition.value),
      range:
        relativeRange ||
        (condition.startDate && condition.endDate
          ? ([
              convertToDatejs(condition.startDate),
              convertToDatejs(condition.endDate),
            ] as [any, any])
          : null),
    };
  }
  return {
    condition: null,
    mode: null,
    relativePreset: null,
    date: null,
    range: null,
  };
};

export const parseAndCreateNumericFilter = (
  condition: NumericCondition,
  value: any,
): NumericFilterCondition => {
  let actualValue: number | undefined;
  let actualMin: number | undefined;
  let actualMax: number | undefined;

  if (condition === "between" && typeof value === "object" && value !== null) {
    actualValue = undefined;
    actualMin = value.min;
    actualMax = value.max;
  } else if (condition === "has_data" || condition === "no_data") {
    actualValue = undefined;
    actualMin = undefined;
    actualMax = undefined;
  } else {
    actualValue = value ?? undefined;
    actualMin = undefined;
    actualMax = undefined;
  }

  return createNumericFilter(condition, actualValue, actualMin, actualMax);
};

export const conditions = [
  { value: "equals", label: "= (Equals)", needsValue: true },
  { value: "not_equals", label: "≠ (Does not equal)", needsValue: true },
  { value: "greater_than", label: "> (Greater than)", needsValue: true },
  { value: "less_than", label: "< (Less than)", needsValue: true },
  {
    value: "greater_than_or_equal",
    label: "≥ (Greater than or equal to)",
    needsValue: true,
  },
  {
    value: "less_than_or_equal",
    label: "≤ (Less than or equal to)",
    needsValue: true,
  },
  { value: "has_data", label: "Has data", needsValue: false },
  { value: "no_data", label: "Does not have data", needsValue: false },
  { value: "between", label: "Between", needsValue: true },
];

export const mapConditionValue = (radioValue: string): string => {
  const conditionMap: Record<string, string> = {
    on: "equals",
    before: "before",
    after: "after",
    between: "between",
  };
  return conditionMap[radioValue] ?? radioValue;
};

export const mapDisplayValue = (conditionValue: string): string => {
  const displayMap: Record<string, string> = {
    equals: "on",
    before: "before",
    after: "after",
    between: "between",
  };
  return displayMap[conditionValue] ?? conditionValue;
};
